import CryptoJS from "crypto-js";
import { app_encr_decript_sec_key } from "../MasterData/config";

export const decrypt = (encrypted) => {
  try {
    const decrypted = CryptoJS.AES.decrypt(
      encrypted,
      app_encr_decript_sec_key
    ).toString(CryptoJS.enc.Utf8);
    return decrypted;
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

export let encrypt = (text) => {
  try {
    const encrypted = CryptoJS.AES.encrypt(
      text,
      app_encr_decript_sec_key
    ).toString();
    return encrypted;
  } catch (error) {
    console.error("Encryption error:", error);
    return null;
  }
};

/**
 * Decode JWT token without verification
 * @param {string} token - The JWT token to decode
 * @returns {object|null} - The decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    if (!token) {
      console.error("No token provided");
      return null;
    }

    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64 decode (handle URL-safe base64)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("JWT decode error:", error);
    return null;
  }
};

/**
 * Decode and return the full payload from the stored JWT auth token.
 * Use this as the single source of truth for any token claim
 * (e.g. clientId, menuList, roles, userId, …).
 *
 * @returns {object|null} - The decoded JWT payload, or null if unavailable / invalid
 */
export const getAuthTokenPayload = () => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    return decodeJWT(token);
  } catch (error) {
    console.error("Error decoding auth token payload:", error);
    return null;
  }
};

// Legacy per-user permission menuId, keyed by menu name - loginApp.menuPermissions
// (see getOwnMenuList below) uses a different numbering with no dedicated
// entries, so this lets existing MENU-XXX-keyed checks keep working unchanged.
const OWN_MENU_NAME_TO_ID = {
  "Dashboard": "MENU-001",
  "Run Post": "MENU-002",
  "Safe Post": "MENU-003",
  "Approvals": "MENU-006",
  "Marketing Assets": "MENU-009",
  "Secure Accounts": "MENU-010",
  "Users": "MENU-007",
  "Settings": "MENU-008",
  "Calendar Schedule": "MENU-011",
};

/**
 * Returns the current session's own menu permissions, normalized to the
 * legacy shape ({ menuId, menuName, permission, readAccess, writeAccess })
 * that the sidebar, route guards, and per-page permission checks already
 * expect - sourced from the JWT's loginApp.menuPermissions, which uses a
 * different menuId numbering, a single permission field (no readAccess/
 * writeAccess split), and carries none of the old plan-specific extras
 * (allowPerMonthPost, storageLimit, userLimit, etc.) - those extras are
 * simply absent on the returned entries now.
 *
 * A menu with no entry in loginApp.menuPermissions (e.g. Email/SMS Campaign,
 * which the new token doesn't send at all) is just missing from the result,
 * which every existing consumer already treats as "no access".
 */
export const getOwnMenuList = () => {
  const menuPermissions = getAuthTokenPayload()?.loginApp?.menuPermissions ?? [];
  return menuPermissions.map((menu) => {
    const permission = menu.permission === "read" || menu.permission === "write"
      ? menu.permission
      : "no";
    return {
      menuId: OWN_MENU_NAME_TO_ID[menu.menuName] || menu.menuId,
      menuName: menu.menuName,
      permission,
      readAccess: permission === "read" || permission === "write" ? "yes" : "no",
      writeAccess: permission === "write" ? "yes" : "no",
    };
  });
};

const MENU_ID_TO_ROUTE = {
  "MENU-001": "/dashboard",
  "MENU-002": "/run-post",
  "MENU-003": "/safe-post",
  "MENU-006": "/approvals",
  "MENU-007": "/users",
  "MENU-008": "/settings",
  "MENU-009": "/marketing-assets",
  "MENU-010": "/secure-accounts",
  "MENU-011": "/calendar-schedule",
};

/**
 * Route to send a logged-in user to - the first menu they have read or
 * write access to, falling back to /dashboard if none matched or nothing
 * is accessible. Used both to land an already-authenticated visitor coming
 * from the marketing page and to pick where the sidebar's home icon goes.
 */
export const getDefaultRouteForUser = () => {
  const firstAccessible = getOwnMenuList().find(
    (m) => m.readAccess === "yes" || m.writeAccess === "yes"
  );
  return (firstAccessible && MENU_ID_TO_ROUTE[firstAccessible.menuId]) || "/dashboard";
};

/**
 * This app has no login screen of its own when reached from the auth app -
 * the auth app appends userToken/isAuthenticated/loginTime as URL params
 * instead. Read them into localStorage (under the "authToken" key, which is
 * what the rest of this app reads via getAuthTokenPayload) and strip them
 * from the URL.
 *
 * Must run before the app renders anything (see src/index.js) - components
 * like Header read the token synchronously during render, and a localStorage
 * write alone doesn't trigger a re-render, so consuming it later (e.g. in a
 * useEffect) leaves those components stuck with the stale token until
 * something else forces a re-render (like a manual page refresh).
 */
export const consumeAuthFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const userToken = params.get("userToken");
  if (!userToken) return;

  localStorage.setItem("authToken", userToken);
  localStorage.setItem("isAuthenticated", params.get("isAuthenticated") || "true");
  localStorage.setItem("loginTime", params.get("loginTime") || Date.now().toString());

  params.delete("userToken");
  params.delete("isAuthenticated");
  params.delete("loginTime");
  const remaining = params.toString();
  const cleanUrl =
    window.location.pathname + (remaining ? `?${remaining}` : "") + window.location.hash;
  window.history.replaceState({}, "", cleanUrl);
};
